/*
 * drawer-guard.js
 *
 * Site-wide ownership of the FILTER drawer, v2: authorization gate.
 *
 * History: the theme's delegated handler (theme.js:5657,
 *   $doc.on('click', '[data-sidebar]', ... addClass('open-mobile-sidebar')))
 * plus its resize-driven mobile re-init (theme.js:188, crossing 1024px
 * calls menuSidebarMobile() and friends, which move DOM nodes and
 * rebind handlers) kept popping the drawer open with no user click.
 * Three click-blocking attempts failed because the trigger isn't
 * always a click.
 *
 * New model, trigger-agnostic:
 *   - The drawer's VISIBLE state (collection-claude-code.css) requires
 *     body.k-drawer-open, a class that ONLY this file ever sets.
 *     Whatever the theme does to `open-mobile-sidebar`, the drawer
 *     cannot become visible without our authorization class.
 *   - `open-mobile-sidebar` is still applied alongside (the theme's
 *     CSS uses it for the scroll lock and its own overlay), but any
 *     appearance of it WITHOUT k-drawer-open is stripped on sight,
 *     so it can never lock scrolling from an unauthorized path.
 *
 * Open path (the only one): a real click on a former [data-sidebar]
 * button, which we've taken over (attribute stripped so the theme's
 * delegate can never match; our own listener bound instead).
 *
 * Close paths: theme X button, overlay click, [data-close-sidebar],
 * the APPLY button (collection-claude-code.js removes
 * open-mobile-sidebar; the enforcer then clears k-drawer-open),
 * and the theme's own outside-click handler.
 */
(function () {
  'use strict';

  var OPEN = 'open-mobile-sidebar';
  var AUTH = 'k-drawer-open';

  function openDrawer() {
    // AUTH first so the class observer never sees OPEN unauthorized.
    document.body.classList.add(AUTH);
    document.body.classList.add(OPEN);
  }

  function closeDrawer() {
    document.body.classList.remove(OPEN);
    document.body.classList.remove(AUTH);
  }

  function takeOverSidebarButtons() {
    document.querySelectorAll('[data-sidebar]').forEach(function (el) {
      if (el.dataset.kOwnsSidebar === '1') return;
      el.dataset.kOwnsSidebar = '1';
      el.removeAttribute('data-sidebar');
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
      });
    });
  }

  // Explicit close paths (capture phase, before any theme handler).
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (
      t.closest('[data-close-sidebar]') ||
      t.closest('.halo-sidebar-close') ||
      t.closest('.background-overlay')
    ) {
      closeDrawer();
    }
  }, true);

  // The enforcer. Runs on every body class change:
  //   OPEN without AUTH  -> unauthorized open attempt, strip it
  //                         (prevents both drawer flash and the stuck
  //                         scroll lock that read as "can't scroll up").
  //   AUTH without OPEN  -> some code closed the drawer (theme X,
  //                         outside click, APPLY); clear authorization
  //                         so the state machine resets.
  function enforce() {
    var b = document.body.classList;
    if (b.contains(OPEN) && !b.contains(AUTH)) {
      b.remove(OPEN);
    }
    if (b.contains(AUTH) && !b.contains(OPEN)) {
      b.remove(AUTH);
    }
  }

  function init() {
    takeOverSidebarButtons();
    enforce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

  // Any DOM insertion (Section Rendering swaps innerHTML after filter
  // changes) can bring back [data-sidebar] buttons; take them over
  // before they can be clicked.
  new MutationObserver(function () {
    if (document.querySelector('[data-sidebar]')) {
      takeOverSidebarButtons();
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  new MutationObserver(enforce).observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  });
})();
